/**
 * Maps Gobodo / RDS CreditBlock{suffix} records to CreditBlockProduct (SF CreditBlock__c parity).
 */

import type { CreditBlockProduct } from '../../types';

function pickString(r: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = r[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function pickNum(r: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = r[k];
    if (v == null || v === '') continue;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function pickDateSlice(r: Record<string, unknown>, ...keys: string[]): string {
  const s = pickString(r, ...keys);
  return s ? s.slice(0, 10) : '';
}

/** Normalize SF Stage__c / Gobodo stage to hub status chip. */
export function creditBlockStageToStatus(stageRaw: string): CreditBlockProduct['status'] {
  const s = stageRaw.toLowerCase().trim();
  if (s.includes('cancelled')) return 'cancelled';
  if (
    s.includes('suspended') ||
    s.includes('nsf') ||
    s.includes('minor on-hold') ||
    s.includes('deceased')
  ) {
    return 'suspended';
  }
  return 'active';
}

export function mapCreditBlockRecordToProduct(cb: Record<string, unknown>, i: number): CreditBlockProduct {
  const data = ((cb.data as Record<string, unknown> | undefined) ?? {}) as Record<string, unknown>;
  const suffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
  const src = { ...data, ...cb } as Record<string, unknown>;
  const stageLabel = pickString(src, 'Stage__c', `Stage${suffix}`, 'stage', 'custom__stage', 'SubscriptionStage__c');
  const status = creditBlockStageToStatus(stageLabel || 'active');
  const start =
    pickDateSlice(src, 'StartDate__c', 'Start_Date__c', `Start_Date${suffix}`, 'startDate', 'custom__start_date', 'EnrollmentDate__c') ||
    '';
  const end = pickDateSlice(src, 'EndDate__c', 'End_Date__c', `End_Date${suffix}`, 'endDate', 'custom__end_date');
  const rawKey = pickString(src, 'key');
  const rawRecordId = pickString(src, 'id', 'sf_id', 'Id', 'SubscriptionId__c');
  const rawId = rawKey || rawRecordId;
  const subId =
    typeof rawId === 'string' && /CB-SUB|cb-sub/i.test(rawId) ? rawId : `CB-SUB-${String(rawId ?? i)}`;

  return {
    id: subId,
    recordId: rawRecordId || undefined,
    recordKey: rawKey || undefined,
    name: pickString(src, 'Name__c', 'name') || 'Credit Block',
    enrollmentDate: start || new Date().toISOString().slice(0, 10),
    endDate: end || undefined,
    status,
    subscriptionStage: stageLabel || undefined,
    creditProgramType: pickString(
      src,
      'Credit_Program_Type__c',
      `Credit_Program_Type${suffix}`,
      'custom__credit_program_type',
      'custom__credit_program_type__c'
    ) || undefined,
    subscriptionEmail: pickString(src, 'Email__c', 'email__c', 'custom__email') || undefined,
    initialLockdownDate:
      pickDateSlice(src, 'Initial_Lockdown_Date__c', `Initial_Lockdown_Date${suffix}`, 'custom__initial_lockdown_date') || undefined,
    optOutPrescreenStartDate:
      pickDateSlice(src, 'Opt_out_Prescreen_Start_Date__c', `Opt_out_Prescreen_Start_Date${suffix}`, 'custom__opt_out_prescreen_start_date') ||
      undefined,
    optOutPrescreenStatus:
      pickString(src, 'Opt_out_Prescreen_Status__c', `Opt_out_Prescreen_Status${suffix}`, 'custom__opt_out_prescreen_status') || undefined,
    priorityNotes: pickString(src, 'Priority_Notes__c', `Priority_Notes${suffix}`, 'custom__priority_notes') || undefined,
    openCases: pickNum(src, 'Open_Cases__c', `Open_Cases${suffix}`, 'custom__open_cases'),
    activeCasesOpen: pickNum(src, 'Active_Cases_Open__c', `Active_Cases_Open${suffix}`, 'custom__active_cases_open'),
    createdBy:
      pickString(
        src,
        'CreatedByName',
        'createdByName',
        'CreatedById__c',
        'CreatedById',
        'created_by_id',
        'CreatedBy__c',
        'createdBy'
      ) || undefined,
    createdDate:
      pickString(src, 'CreatedDate__c', 'CreatedDate', `Created_Date${suffix}`, 'createdDate', 'created_date', 'custom__created_date') || undefined,
    lastModifiedBy:
      pickString(
        src,
        'LastModifiedByName',
        'lastModifiedByName',
        'LastModifiedById__c',
        'LastModifiedById',
        'last_modified_by_id',
        'LastModifiedBy__c',
        'lastModifiedBy'
      ) || undefined,
    lastModifiedDate:
      pickString(src, 'LastModifiedDate__c', 'LastModifiedDate', `Last_Modified_Date${suffix}`, 'lastModifiedDate', 'last_modified_date', 'custom__last_modified_date') || undefined,
  };
}
